// Sound system for webolo.
//
// Mirrors winbolo's sounddist.c distance logic:
//   SDIST_SOFT = 15 tiles  — beyond this, play "far" variant
//   SDIST_NONE = 40 tiles  — beyond this, play nothing
//
// Each sound type maps to a near file and an optional far file.
// Self-events (shootSelf, hitTankSelf, bubbles) have no far variant and
// always play regardless of distance.

const SDIST_SOFT = 15;
const SDIST_NONE = 40;

export type SoundType =
  | "shootSelf"
  | "shootNear"
  | "shotTreeNear"
  | "shotBuildingNear"
  | "hitTankNear"
  | "hitTankSelf"
  | "bubbles"
  | "tankSinkNear"
  | "bigExplosionNear"
  | "farmingTreeNear"
  | "manBuildingNear"
  | "manDyingNear"
  | "manLayingMineNear"
  | "mineExplosionNear";

// [near, far | null]. null = no far variant; plays near-only within range.
const FILES: Record<SoundType, [string, string | null]> = {
  shootSelf:        ["shooting_self.wav",      null],
  shootNear:        ["shooting_near.wav",       "shooting_far.wav"],
  shotTreeNear:     ["shot_tree_near.wav",      "shot_tree_far.wav"],
  shotBuildingNear: ["shot_building_near.wav",  "shot_building_far.wav"],
  hitTankNear:      ["hit_tank_near.wav",       "hit_tank_far.wav"],
  hitTankSelf:      ["hit_tank_self.wav",       null],
  bubbles:          ["bubbles.wav",             null],
  tankSinkNear:     ["tank_sinking_near.wav",   "tank_sinking_far.wav"],
  bigExplosionNear: ["big_explosion_near.wav",  "big_explosion_far.wav"],
  farmingTreeNear:  ["farming_tree_near.wav",   "farming_tree_far.wav"],
  manBuildingNear:  ["man_building_near.wav",   "man_building_far.wav"],
  manDyingNear:     ["man_dying_near.wav",      "man_dying_far.wav"],
  manLayingMineNear:["man_lay_mine_near.wav",   null],
  mineExplosionNear:["mine_explosion_near.wav", "mine_explosion_far.wav"],
};

export class SoundSystem {
  private actx: AudioContext | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private loading = false;
  private muted = false;

  // Call from any user gesture (keydown, click) to satisfy browser autoplay policy.
  unlock(): void {
    if (!this.actx) {
      this.actx = new AudioContext();
      if (!this.loading) {
        this.loading = true;
        this.loadAll();
      }
    } else if (this.actx.state === "suspended") {
      this.actx.resume().catch(() => {});
    }
  }

  private loadAll(): void {
    const all = new Set<string>();
    for (const [near, far] of Object.values(FILES)) {
      all.add(near);
      if (far) all.add(far);
    }
    for (const f of all) this.loadFile(f).catch(() => {});
  }

  private async loadFile(filename: string): Promise<void> {
    const ctx = this.actx;
    if (!ctx) return;
    try {
      const res = await fetch(`/sounds/${filename}`);
      if (!res.ok) return;
      const ab = await res.arrayBuffer();
      const buf = await ctx.decodeAudioData(ab);
      this.buffers.set(filename, buf);
    } catch {
      // Non-fatal — game works without audio.
    }
  }

  // Play a positional sound event. selfTileX/Y is the local player's tile
  // coordinate; eventTileX/Y is where the sound originates.
  // Pass selfTileX = eventTileX = 0 (any equal values) for self-events.
  playEvent(
    type: SoundType,
    eventTileX: number,
    eventTileY: number,
    selfTileX: number,
    selfTileY: number,
  ): void {
    if (!this.actx || this.muted) return;

    const gapX = Math.abs(eventTileX - selfTileX);
    const gapY = Math.abs(eventTileY - selfTileY);

    // Outside hearing range — play nothing.
    if (gapX >= SDIST_NONE || gapY >= SDIST_NONE) return;

    const [nearFile, farFile] = FILES[type];

    let file: string;
    if (type === "manLayingMineNear") {
      // Only plays within soft range.
      if (gapX > SDIST_SOFT || gapY > SDIST_SOFT) return;
      file = nearFile;
    } else if (farFile && (gapX > SDIST_SOFT || gapY > SDIST_SOFT)) {
      file = farFile;
    } else {
      file = nearFile;
    }

    this.playBuffer(file);
  }

  private playBuffer(filename: string): void {
    const ctx = this.actx;
    if (!ctx) return;
    const buf = this.buffers.get(filename);
    if (!buf) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start();
  }

  setMuted(v: boolean): void {
    this.muted = v;
  }

  get isMuted(): boolean {
    return this.muted;
  }
}
